import { useState, type ChangeEvent, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type EnrollmentStatus,
  type PanelDecisionType,
  type RecordDecisionRequest,
} from '@ctms/contracts';
import { AlertCircle, Stethoscope } from 'lucide-react';

import { getApiErrorMessage } from '../../../../api/client.js';
import { DECISION_TYPE_LABELS, PARTICIPATION_STATUS_LABELS } from '../../decision-labels.js';
import { patientsApi } from '../../patients.api.js';
import { patientQueryKeys } from '../../patients.query-keys.js';

const fieldClass =
  'w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100 scheme-dark';

/**
 * Target status per panel decision (Q-PAT-008 matrix, frozen at the
 * 2026-10-21 gate) — rendered ONLY in the confirmation modal text. The
 * backend `DecisionsService` stays the single source of truth for the
 * transition; this mirror exists because the panel read returns the
 * available decision types without their targets. Stage decisions carry
 * `null` — history entry only, no status change.
 */
const DECISION_TARGET_STATUS: Record<PanelDecisionType, EnrollmentStatus | null> = {
  enroll_in_treatment: 'ongoing',
  stage_improvement: null,
  stage_no_improvement: null,
  end_treatment: 'follow_up',
  withdraw_consent: 'withdrawn',
  end_follow_up: 'completed',
  lost_contact: 'lost_to_follow_up',
};

/** `createdAt` (ISO) → the wireframe's local `YYYY-MM-DD HH:mm` form. */
function formatDecisionTimestamp(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

export interface DecisionsPanelProps {
  /** The chart owning the participation (`patients.id`). */
  patientId: number;
  /** The participation whose decisions are shown and recorded (`trial_enrollments.id`). */
  enrollmentId: number;
  /** Presentation name of the patient (e.g. `Anna Testowa`). */
  patientName: string;
  /**
   * `patients:write` of the signed-in user. A read-only user still BROWSES
   * the history (wireframe: „Przy uprawnieniu tylko do odczytu — historia
   * jest przeglądana, kontrolki decyzji nie są wyświetlane"); the UI hides
   * the controls and the backend enforces the write (403).
   */
  canWrite: boolean;
}

/**
 * The „Udział w badaniu" section of the patient card (issue #29,
 * REQ-PAT-014/032/033; approved wireframe
 * `spec/domains/patients-pathways/ui/treatment-decisions.ui.md`). Inline in
 * the card — no separate windows (cockpit principle); the only modal is the
 * per-decision confirmation (gate recommendation 4). Recording a decision
 * NEVER touches the visit schedule (rescope 2026-10-21 — the schedule stays
 * purely manual). The availability matrix lives in the backend: the select
 * offers exactly what the panel read returned in `availableDecisions`.
 */
export function DecisionsPanel({
  patientId,
  enrollmentId,
  patientName,
  canWrite,
}: DecisionsPanelProps): JSX.Element {
  const queryClient = useQueryClient();
  const [selectedDecision, setSelectedDecision] = useState<PanelDecisionType | null>(null);
  const [comment, setComment] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const panelQuery = useQuery({
    queryKey: patientQueryKeys.decisions(patientId, enrollmentId),
    queryFn: () => patientsApi.getDecisionPanel(patientId, enrollmentId),
  });

  const recordMutation = useMutation({
    mutationFn: (request: RecordDecisionRequest) =>
      patientsApi.recordDecision(patientId, enrollmentId, request),
  });

  const data = panelQuery.data;
  const available = data?.availableDecisions ?? [];
  // A concurrent decision may shrink the matrix under a stale local pick —
  // fall back to the first available (the backend re-validates anyway, 409).
  const effectiveDecision =
    selectedDecision !== null && available.includes(selectedDecision)
      ? selectedDecision
      : (available[0] ?? null);
  const isTerminal = data !== undefined && available.length === 0;
  const targetStatus =
    effectiveDecision === null ? null : DECISION_TARGET_STATUS[effectiveDecision];
  const confirmationStatusText =
    data === undefined || effectiveDecision === null
      ? ''
      : targetStatus === null
        ? `Status udziału nie zmieni się — decyzja zostanie zapisana wyłącznie w historii (pozostaje „${PARTICIPATION_STATUS_LABELS[data.status]}”).`
        : `Status udziału zmieni się z „${PARTICIPATION_STATUS_LABELS[data.status]}” na „${PARTICIPATION_STATUS_LABELS[targetStatus]}”.`;

  function handleDecisionChange(event: ChangeEvent<HTMLSelectElement>): void {
    setSelectedDecision(event.target.value as PanelDecisionType);
  }

  function handleCommentChange(event: ChangeEvent<HTMLInputElement>): void {
    setComment(event.target.value);
  }

  /** One atomic write: history row + (for status decisions) the transition. */
  function submitDecision(): void {
    if (effectiveDecision === null) {
      return;
    }
    const trimmed = comment.trim();
    recordMutation.mutate(
      { decisionType: effectiveDecision, comment: trimmed.length === 0 ? null : trimmed },
      {
        onSuccess: async () => {
          setSaveError(null);
          setIsConfirmOpen(false);
          setSelectedDecision(null);
          setComment('');
          // The status chip of the participation bar and the chart header read
          // the enrollments/detail caches — refresh all three seams.
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: patientQueryKeys.decisions(patientId, enrollmentId),
            }),
            queryClient.invalidateQueries({
              queryKey: patientQueryKeys.enrollments(patientId),
            }),
            queryClient.invalidateQueries({
              queryKey: patientQueryKeys.detail(patientId),
            }),
          ]);
        },
        onError: async (error: unknown) => {
          // Nothing was written (atomicity) — the form keeps its state and the
          // error modal offers the retry of the very same request.
          setIsConfirmOpen(false);
          setSaveError(await getApiErrorMessage(error, 'Zapis decyzji nie powiódł się.'));
        },
      },
    );
  }

  return (
    <section
      aria-labelledby="decisions-panel-title"
      className="mt-4 border-t border-slate-800 pt-4"
    >
      <div className="flex items-start justify-between gap-3">
        <h3
          id="decisions-panel-title"
          className="flex items-center gap-2 text-sm font-semibold text-slate-100"
        >
          <Stethoscope className="h-4 w-4 text-cyan-400" />
          {canWrite ? 'Udział w badaniu' : 'Udział w badaniu (tylko do odczytu)'}
        </h3>
        <p className="flex items-center gap-1.5 text-xs text-slate-400">
          <span className="font-medium text-slate-200">{patientName}</span>
          {data !== undefined ? (
            <>
              Status udziału:
              <span className="rounded-full border border-slate-700 bg-slate-950 px-2 py-0.5 text-[11px] font-medium text-cyan-300">
                {PARTICIPATION_STATUS_LABELS[data.status]}
              </span>
            </>
          ) : null}
        </p>
      </div>

      {panelQuery.isLoading ? (
        <div role="status" className="mt-3 space-y-2">
          <p className="text-xs text-slate-400">Ładowanie statusu udziału i historii decyzji…</p>
          <div className="h-16 animate-pulse rounded-md bg-slate-800/60" />
          <div className="h-24 animate-pulse rounded-md bg-slate-800/40" />
        </div>
      ) : panelQuery.isError ? (
        <div
          role="alert"
          className="mt-3 rounded-md border border-rose-800/70 bg-rose-950/40 p-3"
        >
          <p className="text-xs text-rose-200">Nie udało się wczytać historii decyzji.</p>
          <button
            type="button"
            onClick={() => {
              void panelQuery.refetch();
            }}
            className="mt-2 rounded-md border border-rose-700 px-3 py-1.5 text-xs text-rose-200"
          >
            Spróbuj ponownie
          </button>
        </div>
      ) : data !== undefined ? (
        <>
          {!canWrite ? (
            <p className="mt-3 text-xs text-slate-400">
              <span className="font-medium text-slate-300">
                Nie masz uprawnienia do zapisu pacjentów.
              </span>{' '}
              Historia decyzji jest widoczna do odczytu.
            </p>
          ) : isTerminal ? (
            <p className="mt-3 text-xs text-slate-400">
              <span className="font-medium text-slate-300">
                Udział jest w statusie terminalnym — zapisywanie decyzji nie jest możliwe.
              </span>{' '}
              Historia pozostaje do odczytu.
            </p>
          ) : (
            <div className="mt-3">
              <h4 className="text-xs font-semibold text-slate-200">Decyzja lekarza</h4>
              <div className="mt-2 flex flex-wrap items-end gap-3">
                <label className="min-w-72 text-xs text-slate-400">
                  Decyzja
                  <select
                    value={effectiveDecision ?? ''}
                    onChange={handleDecisionChange}
                    className={`${fieldClass} mt-1 cursor-pointer`}
                  >
                    {available.map((type) => (
                      <option key={type} value={type}>
                        {DECISION_TYPE_LABELS[type]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="min-w-64 flex-1 text-xs text-slate-400">
                  Komentarz
                  <input
                    type="text"
                    value={comment}
                    onChange={handleCommentChange}
                    placeholder="Komentarz do decyzji (opcjonalnie)"
                    className={`${fieldClass} mt-1`}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setIsConfirmOpen(true)}
                  disabled={effectiveDecision === null || recordMutation.isPending}
                  className="rounded border border-cyan-700/70 bg-cyan-950/40 px-2.5 py-1.5 text-xs font-medium text-cyan-300 hover:bg-cyan-900/60 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Zapisz decyzję
                </button>
              </div>
              <p className="mt-2 text-[11px] text-slate-500">
                Zapisanie decyzji nie dodaje, nie usuwa ani nie przesuwa wizyt — harmonogram
                prowadzi personel ręcznie z katalogu protokołu.
              </p>
            </div>
          )}

          <div className="mt-4">
            <h4 className="text-xs font-semibold text-slate-200">
              Historia decyzji (tylko do odczytu)
            </h4>
            {data.items.length === 0 ? (
              <p className="mt-2 text-xs text-slate-400">
                <span className="font-medium text-slate-300">Brak zapisanych decyzji.</span>{' '}
                Udział został właśnie utworzony — pierwszy wpis pojawi się po zapisaniu decyzji
                kwalifikacyjnej (dialog karty #30) albo innej decyzji lekarza.
              </p>
            ) : (
              <table className="mt-2 w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-800 text-[11px] tracking-wide text-slate-500">
                    <th scope="col" className="px-2 py-1.5 font-medium">
                      Data i czas
                    </th>
                    <th scope="col" className="px-2 py-1.5 font-medium">
                      Kto
                    </th>
                    <th scope="col" className="px-2 py-1.5 font-medium">
                      Decyzja
                    </th>
                    <th scope="col" className="px-2 py-1.5 font-medium">
                      Zmiana statusu
                    </th>
                    <th scope="col" className="px-2 py-1.5 font-medium">
                      Komentarz
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr key={item.id} className="border-b border-slate-800/60 text-slate-300">
                      <td className="px-2 py-1.5 whitespace-nowrap text-slate-400">
                        {formatDecisionTimestamp(item.createdAt)}
                      </td>
                      <td className="px-2 py-1.5">{item.userDisplayName ?? '—'}</td>
                      <td className="px-2 py-1.5">{DECISION_TYPE_LABELS[item.decisionType]}</td>
                      <td className="px-2 py-1.5">
                        {item.fromStatus === null || item.toStatus === null
                          ? 'Bez zmiany'
                          : `${PARTICIPATION_STATUS_LABELS[item.fromStatus]} → ${PARTICIPATION_STATUS_LABELS[item.toStatus]}`}
                      </td>
                      <td className="px-2 py-1.5 text-slate-400">{item.comment ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      {isConfirmOpen && data !== undefined && effectiveDecision !== null ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="decision-confirm-title"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 p-4"
        >
          <div className="w-full max-w-sm rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
            <h3 id="decision-confirm-title" className="text-sm font-semibold text-slate-100">
              {DECISION_TYPE_LABELS[effectiveDecision]} — potwierdzenie
            </h3>
            <p className="mt-2 text-xs text-slate-300">{patientName}</p>
            <p className="mt-2 text-xs text-slate-400">
              {confirmationStatusText} Decyzja zostanie zapisana w historii razem z aktorem i
              czasem. Harmonogram wizyt nie zmieni się.
            </p>
            <p className="mt-2 text-xs font-semibold text-slate-200">
              Decyzja jest nieodwracalna w tym panelu.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsConfirmOpen(false)}
                className="rounded-md border border-slate-700 px-3.5 py-1.5 text-xs font-medium text-slate-300 hover:bg-slate-800"
              >
                Wróć
              </button>
              <button
                type="button"
                onClick={submitDecision}
                disabled={recordMutation.isPending}
                className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Potwierdź i zapisz
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {saveError !== null ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="decision-error-title"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/70 p-4"
        >
          <div className="w-full max-w-sm rounded-xl border border-rose-900/60 bg-slate-900 p-6 text-slate-100 shadow-2xl">
            <h3
              id="decision-error-title"
              className="flex items-center gap-2 text-sm font-semibold text-rose-200"
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              Nie udało się wykonać operacji
            </h3>
            <p className="mt-2 text-xs text-slate-200">{saveError}</p>
            <p className="mt-2 text-xs text-slate-400">
              Status udziału nie zmienił się, w historii nie pojawił się nowy wpis, a harmonogram
              wizyt pozostał bez zmian. Możesz bezpiecznie spróbować ponownie.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSaveError(null)}
                className="rounded-md border border-slate-700 px-3.5 py-1.5 text-xs font-medium text-slate-300 hover:bg-slate-800"
              >
                Zamknij
              </button>
              <button
                type="button"
                onClick={submitDecision}
                disabled={recordMutation.isPending}
                className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Spróbuj ponownie
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
